import { useEffect, useRef } from "react";

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: Record<string, unknown>,
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_ID = "cloudflare-turnstile-script";
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  return new Promise((resolve, reject) => {
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const ready = () => window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile belum siap"));
    const failed = () => reject(new Error("Turnstile gagal dimuat"));
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.addEventListener("load", ready, { once: true });
      script.addEventListener("error", failed, { once: true });
      document.head.appendChild(script);
      return;
    }
    script.addEventListener("load", ready, { once: true });
    script.addEventListener("error", failed, { once: true });
  });
}

export function TurnstileWidget({
  siteKey,
  action,
  resetKey,
  onToken,
  onError,
  label = "Pemeriksaan keamanan",
}: {
  siteKey: string;
  action: string;
  resetKey: number;
  onToken: (token: string) => void;
  onError: (message: string) => void;
  label?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);
  const tokenCallbackRef = useRef(onToken);
  const errorCallbackRef = useRef(onError);
  const initialResetKey = useRef(resetKey);

  useEffect(() => {
    tokenCallbackRef.current = onToken;
    errorCallbackRef.current = onError;
  }, [onError, onToken]);

  useEffect(() => {
    let cancelled = false;
    void loadTurnstile()
      .then((api) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = api.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme: "light",
          size: "flexible",
          appearance: "always",
          "response-field": false,
          callback: (token: string) => tokenCallbackRef.current(token),
          "expired-callback": () => tokenCallbackRef.current(""),
          "error-callback": () => {
            tokenCallbackRef.current("");
            errorCallbackRef.current("Pemeriksaan keamanan belum berhasil dimuat. Coba lagi.");
          },
        });
      })
      .catch(() => errorCallbackRef.current("Pemeriksaan keamanan belum dapat dimuat. Periksa internet lalu coba lagi."));
    return () => {
      cancelled = true;
      const widgetId = widgetIdRef.current;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
      widgetIdRef.current = undefined;
    };
  }, [action, siteKey]);

  useEffect(() => {
    if (resetKey === initialResetKey.current) return;
    initialResetKey.current = resetKey;
    const widgetId = widgetIdRef.current;
    if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
  }, [resetKey]);

  return <div className="turnstile-widget" ref={containerRef} aria-label={label} />;
}
